import React, { useEffect, useState, useCallback } from "react";
import { connect } from 'react-redux';
import NetworkZoneForm from "./NetworkZoneForm";
import NetworkZoneConfirmationDialog from "./NetworkZoneConfirmationDialog";
import { CircularProgress } from "@material-ui/core";
import * as Yup from "yup";
import { parseURL } from "../../sc-utilities";
import * as actions from '../store/networkZoneActions';
import History from "../../../History";
import { hasPermission } from "../../sc-utilities";
import { CancelToken } from 'axios';

const NetworkZoneEdit = props => {
    const { activeCustomerID, translations, whoIs, listPermissions, loadNetworkZone, networkZone, loadNetworkZoneInit, updateNetworkZone, permissions: userPermissions } = props;
    const [editData, setEditData] = useState({ permissions: [], formattedPermissions: [], loadingData: true, UserIP: null });
    const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
    const [dataToSend, setDataToSend] = useState(null);
    const [confirming, setConfirming] = useState(false);

    useEffect(() => {
        const requestToken = CancelToken.source();
        const fetchData = async () => {
            await loadNetworkZone({ ActiveCustomerID: activeCustomerID, NetworkZoneID: parseURL(window.location.href).NetworkZoneID }, { cancelToken: requestToken.token })
        }
        fetchData();
        return () => {
            requestToken.cancel('Operation canceled by the user.');
            loadNetworkZoneInit();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const formatPermissions = useCallback(async (permissions = [], networkZonePermissions = []) => {
        let networkZonePermissionIDs = networkZonePermissions.map(permission => permission.PermissionID);
        return permissions.filter(permission => networkZonePermissionIDs.includes(permission.value));
    }, []);

    const formatNetworkZoneData = useCallback(async (networkZone, requestToken) => {
        let UserIP = await whoIs();
        let permissions = await listPermissions({ ActiveCustomerID: activeCustomerID }, { cancelToken: requestToken.token });
        let formattedPermissions = await formatPermissions(permissions, networkZone.Permissions);
        setEditData({ permissions: permissions, formattedPermissions: formattedPermissions, UserIP: UserIP, loadingData: false });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const requestToken = CancelToken.source();
        if (networkZone && Object.keys(networkZone).length > 0) {
            formatNetworkZoneData(networkZone, requestToken);
        }
        return () => {
            requestToken.cancel('Operation canceled by the user.');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [networkZone]);

    const extractPermissions = selectedPermission => {
        return selectedPermission.map(permission => permission.value);
    };

    const handleSubmit = async data => {
        let formData = { ...data };
        formData['CustomerID'] = activeCustomerID;
        formData['Permissions'] = extractPermissions(formData.Permissions);
        delete formData.FormattedPermissions;
        let result = await updateNetworkZone({ ...formData, ActiveCustomerID: activeCustomerID, NetworkZoneID: parseURL(window.location.href).NetworkZoneID });
        if (result) {
            if (result.ConfirmationNeeded && result.ConfirmationCode) {
                setConfirmationDialogOpen(true);
                setDataToSend({ ...formData, ActiveCustomerID: activeCustomerID, NetworkZoneID: parseURL(window.location.href).NetworkZoneID, ConfirmationCode: result.ConfirmationCode });
                return;
            }
            setEditData({ permissions: [], formattedPermissions: [], loadingData: true });
            History.goBack();
        }
    };


    const handleConfirmTrue = async () => {
        setConfirming(true);
        let result = await updateNetworkZone(dataToSend);
        setDataToSend(null);
        setConfirming(false);
        setConfirmationDialogOpen(false);
        if (result) {
            History.goBack();
        }
    };

    const handleConfirmFalse = () => {
        setConfirming(true);
        setDataToSend(null);
        setConfirming(false);
        setConfirmationDialogOpen(false);
    };

    let initialValues = {
        Name: networkZone && networkZone.Name ? networkZone.Name : '',
        Subnet: networkZone && networkZone.Subnet ? networkZone.Subnet : '',
        Policy: networkZone && networkZone.Policy ? networkZone.Policy : 'allow',
        Priority: networkZone && networkZone.Priority ? networkZone.Priority : 'normal',
        Permissions: editData.formattedPermissions,
        SpecifyPermissions: networkZone ? networkZone.SpecifyPermissions : false,
        DefaultLanguage: networkZone ? networkZone.DefaultLanguage : 'en',
        DefaultKeyboardLayout: networkZone ? networkZone.DefaultKeyboardLayout : 'en'
    };

    let formSettings = {
        Validation: {
            Name: Yup.string().required("Required"),
            Subnet: Yup.string().required("Required"),
            Policy: Yup.string().required("Required"),
            Priority: Yup.string().required("Required"),
            Permissions: Yup.array().when('SpecifyPermissions', {
                is: val => val === true,
                then: Yup.array().min(1).required("Required")
            }),
            SpecifyPermissions: Yup.boolean().required("Required"),
            DefaultLanguage: Yup.string().required("Required"),
            DefaultKeyboardLayout: Yup.string().required("Required")
        },
        FormTitle: translations["edit_network_zone"] || "Edit network zone",
        ButtonSubmit: hasPermission(userPermissions, 'network_zone_save') ? {
            Title: translations["save"] || "SAVE"
        } : null
    };

    return (
        !editData.loadingData ?
            <>
                <NetworkZoneConfirmationDialog
                    translations={translations}
                    confirmationDialogOpen={confirmationDialogOpen}
                    handleConfirmTrue={handleConfirmTrue}
                    handleConfirmFalse={handleConfirmFalse}
                    confirming={confirming}
                    subnet={dataToSend?.Subnet ?? ''}
                />
                <NetworkZoneForm
                    formSettings={formSettings}
                    initialValues={initialValues}
                    handleSubmit={handleSubmit}
                    translations={translations}
                    permissions={editData.permissions}
                    currentIP={editData.UserIP}
                />
            </>
            : <CircularProgress />
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state.core.login.activeCustomer.CustomerID,
        networkZone: state.networkZone.networkZones.loaded,
        translations: state.translate && state.translate.translations ? state.translate.translations.activeTranslations || {} : {},
        permissions: state?.core?.login?.permissions || []
    };
};

const mapDispatchToProps = dispatch => {
    return {
        updateNetworkZone: data => dispatch(actions.editNetworkZone(data)),
        listPermissions: activeCustomerID => dispatch(actions.listPermissions(activeCustomerID)),
        loadNetworkZone: data => dispatch(actions.loadNetworkZone(data)),
        loadNetworkZoneInit: () => dispatch(actions.loadNetworkZoneInit()),
        whoIs: () => dispatch(actions.whoIs())
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(NetworkZoneEdit);
